<script>
  import {
    createEventDispatcher,
    setContext,
    onMount,
    getContext,
    onDestroy,
  } from "svelte";
  import { writable } from "svelte/store";
  import TreeViewNodeList from "./TreeViewNodeList.svelte";

  export let children = [];
  export let activeId = "";
  export let selectedIds = [];
  export let expandedIds = [];
  export let size = "default";
  export let labelText = "";
  export let hideLabel = false;
  export function expandAll() {
    expandedIds = [...nodeIds];
  }
  export function collapseAll() {
    expandedIds = [];
  }

  export function expandNodes(filterNode = (node) => false) {
    expandedIds = nodes
      .filter(
        (node) =>
          filterNode(node) ||
          node.children?.some((child) => filterNode(child) && child.children)
      )
      .map((node) => node.id);
  }

  export function collapseNodes(filterNode = (node) => true) {
    expandedIds = nodes
      .filter((node) => expandedIds.includes(node.id) && !filterNode(node))
      .map((node) => node.id);
  }

  const dispatch = createEventDispatcher();
  const labelId = `label-${Math.random().toString(36)}`;
  const activeNodeId = writable(activeId);
  const selectedNodeIds = writable(selectedIds);
  const expandedNodeIds = writable(expandedIds);
  const parentIds = writable([]);

  let ref = null;
  let treeWalker = null;
  let contextMenuRef;
  let mouseX = 0;
  let mouseY = 0;
  let menuVisible = false;
  let selectedNode = null;

  setContext("TreeView", {
    activeNodeId,
    selectedNodeIds,
    expandedNodeIds,
    parentIds,
    clickNode: (node) => {
      activeId = node.id;
      selectedIds = [node.id];
      dispatch("select", node);
    },
    selectNode: (node) => {
      selectedIds = [node.id];
    },
    expandNode: (node, expanded) => {
      menuVisible = false;
      if (expanded) {
        expandedIds = [...expandedIds, node.id];
      } else {
        expandedIds = expandedIds.filter((_id) => _id !== node.id);
      }
    },
    focusNode: (node) => dispatch("focus", node),
    toggleNode: (node) => {
      menuVisible = false;
      dispatch("toggle", node);
    },
    onContextMenu: ({ node, event, type, action }) => {
      selectedNode = { ...node, type: type, action: action };
      mouseX = event.clientX;
      mouseY = event.clientY;
      menuVisible = true;
    },
  });

  const { clickNode } = getContext("TreeView");
  const { selectedAction } = getContext("TreeViewTest");

  function handleKeyDown(e) {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();

    treeWalker.currentNode = e.target;

    let node = null;

    if (e.key === "ArrowUp") node = treeWalker.previousNode();
    if (e.key === "ArrowDown") node = treeWalker.nextNode();
    if (node && node !== e.target) {
      node.tabIndex = "0";
      node.focus();
    }
  }

  onMount(() => {
    const firstFocusableNode = ref.querySelector(
      "li.bx--tree-node:not(.bx--tree-node--disabled)"
    );

    if (firstFocusableNode != null) {
      firstFocusableNode.tabIndex = "0";
    }
  });

  function traverse(children) {
    let nodes = [];

    children.forEach((node) => {
      nodes.push(node);

      if (Array.isArray(node.children)) {
        nodes = [...nodes, ...traverse(node.children)];
      }
    });

    return nodes;
  }

  function addNewFolder() {
    menuVisible = false;
    if (selectedNode) {
      selectedAction({ action: "new" });
      clickNode({
        ...selectedNode,
        id: "new:" + selectedNode.id,
        action: "new",
      });
    }
  }
  function openDelete() {
    menuVisible = false;
    if (selectedNode) {
      selectedAction({ action: "delete" });
      clickNode({
        ...selectedNode,
        id: "new:" + selectedNode.id,
        action: "delete",
      });
    }
  }

  function handleClickOutside(event) {
    if (contextMenuRef && !contextMenuRef.contains(event.target)) {
      menuVisible = false;
    }
  }

  onMount(() => {
    window.addEventListener("click", handleClickOutside);
  });

  onDestroy(() => {
    window.removeEventListener("click", handleClickOutside);
  });

  $: nodes = traverse(children);
  $: nodeIds = nodes.map((node) => node.id);
  $: activeNodeId.set(activeId);
  $: selectedNodeIds.set(selectedIds);
  $: expandedNodeIds.set(expandedIds);
  $: if (ref) {
    treeWalker = document.createTreeWalker(ref, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node) => {
        if (node.classList.contains("bx--tree-node--disabled"))
          return NodeFilter.FILTER_REJECT;
        if (node.matches("li.bx--tree-node")) return NodeFilter.FILTER_ACCEPT;
        return NodeFilter.FILTER_SKIP;
      },
    });
  }
  $: parentIds.set(children.map(({ id }) => id));
  $: if ($activeNodeId) menuVisible = false;
</script>

{#if !hideLabel}
  <label id={labelId} class:bx--label={true}>
    <slot name="labelText">{labelText}</slot>
  </label>
{/if}

<ul
  {...$$restProps}
  role="tree"
  bind:this={ref}
  class:bx--tree={true}
  class:bx--tree--default={size === "default"}
  class:bx--tree--compact={size === "compact"}
  aria-label={hideLabel ? labelText : undefined}
  aria-labelledby={!hideLabel ? labelId : undefined}
  aria-multiselectable={selectedIds.length > 1 || undefined}
  on:keydown
  on:keydown|stopPropagation={handleKeyDown}
>
  <TreeViewNodeList root {children} />
</ul>

<div
  bind:this={contextMenuRef}
  class="context-menu"
  style="display: {menuVisible
    ? 'block'
    : 'none'}; top: {mouseY}px; left: {mouseX}px;"
>
  <ul>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    {#if selectedNode?.type === "folder"}
      <li on:click|stopPropagation={() => addNewFolder()}>Add New</li>
      <hr />
    {/if}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <li on:click|stopPropagation={() => openDelete()}>Delete</li>
  </ul>
</div>

<style>
  :global(.bx--tree .bx--tree-node) {
    background-color: white !important;
  }
  .context-menu {
    position: fixed;
    background: white;
    border: 1px solid #ccc;
    padding: 5px;
    list-style: none;
    cursor: pointer;
    z-index: 300000;
  }
</style>
